// Analytics & advertising tracking utilities for VAROTRADES.
// Supports: Google Tag Manager (GTM) + Facebook/Meta Pixel (Facebook Ads & Instagram Ads).
// All IDs read from environment variables — production-safe (no hardcoded secrets).
//
// Required env vars (frontend/.env):
//   REACT_APP_GTM_ID                     e.g. GTM-XXXXXXX
//   REACT_APP_FB_PIXEL_ID                e.g. 123456789012345
//   REACT_APP_GOOGLE_SITE_VERIFICATION   e.g. abc...xyz (no quotes)
//   REACT_APP_FB_DOMAIN_VERIFICATION     e.g. abc...xyz
//   REACT_APP_BING_SITE_VERIFICATION     e.g. ABC...XYZ
//   REACT_APP_FB_APP_ID                  e.g. 123456789012345

const GTM_ID = process.env.REACT_APP_GTM_ID;
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID;

let gtmLoaded = false;
let fbPixelLoaded = false;

/* -------------------- Google Tag Manager -------------------- */
export const loadGTM = () => {
  if (gtmLoaded || !GTM_ID || typeof window === 'undefined') return;
  gtmLoaded = true;

  // GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  // Inject GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  // GTM noscript iframe (recommended fallback)
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
};

/* -------------------- Meta / Facebook Pixel -------------------- */
export const loadFBPixel = () => {
  if (fbPixelLoaded || !FB_PIXEL_ID || typeof window === 'undefined') return;
  fbPixelLoaded = true;

  // Standard Facebook Pixel base code
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
    n.queue = []; t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', FB_PIXEL_ID);
  window.fbq('track', 'PageView');

  // noscript pixel fallback for users with JS disabled
  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
};

/* -------------------- Page-View tracking (SPA) -------------------- */
export const trackPageView = (path) => {
  if (typeof window === 'undefined') return;
  if (window.dataLayer) {
    window.dataLayer.push({ event: 'page_view', page_path: path });
  }
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

/* -------------------- Conversion / Event tracking -------------------- */
// Standard FB Pixel events: ViewContent, Lead, CompleteRegistration, AddToCart, Purchase, Contact
// Sends to BOTH GTM dataLayer (Google Ads/Analytics) and Facebook Pixel
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;

  // Google (GTM dataLayer) — custom event
  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...params });
  }

  // Facebook Pixel — send standard or custom event
  if (window.fbq) {
    const STANDARD_EVENTS = [
      'PageView', 'ViewContent', 'Search', 'AddToCart', 'AddToWishlist',
      'InitiateCheckout', 'AddPaymentInfo', 'Purchase', 'Lead',
      'CompleteRegistration', 'Contact', 'Subscribe',
    ];
    if (STANDARD_EVENTS.includes(eventName)) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('trackCustom', eventName, params);
    }
  }
};

// Convenience helpers for the most common conversions
export const trackLead = (params = {}) => trackEvent('Lead', { content_category: 'signup_intent', ...params });
export const trackRegistration = (params = {}) => trackEvent('CompleteRegistration', { content_name: 'account_created', ...params });
export const trackDeposit = (amountUSD, currency = 'USD') => trackEvent('Purchase', { value: amountUSD, currency });
export const trackContact = (channel = 'unknown') => trackEvent('Contact', { content_category: channel });
